// Admin › Resource limits (D83, docs/12 "Resource limits"): the platform default, each role's
// override and each person's. A user's limit is the first of person › role › default; it is the
// size of their new environments and the most they may resize one to. Shown with
// ViewResourceLimits; changed with ManageResourceLimits (the API checks both).
import { Cpu, Info, Plus, UserCircle } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "rizzui";

import { rolesQuery } from "../../auth/admin";
import { usePermissions } from "../../auth/permissions";
import { useT } from "../../i18n";
import { sectionCrumbs, SECTIONS } from "../../layout/nav";
import { toast } from "../../layout/toast";
import { cardClass, IconTile, WidgetCard } from "../../ui/Card";
import { cn } from "../../ui/cn";
import { PageHeader } from "../../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../../ui/states";
import { adminLimitsQuery, type AdminLimits, type LimitTarget, limitFloor, removeLimit, setLimit, toGib } from "./api";
import { type LimitEdit, LimitDialog } from "./LimitDialog";
import { type RoleRow, RolesTable, UsersTable } from "./LimitTables";

type Editing = LimitEdit & { target: LimitTarget | { scope: "new-user" } };

/** Every role (when the admin may list them), with its override; else only the overridden ones. */
function roleRows(limits: AdminLimits, roles: { id: string; name: string }[] | undefined): RoleRow[] {
  const byId = new Map(limits.roles.map((r) => [r.role_id, r]));
  const all = roles ?? limits.roles.map((r) => ({ id: r.role_id, name: r.role_name }));
  return all.map((r) => {
    const o = byId.get(r.id);
    return { id: r.id, name: r.name, limit: o ? { cpus: o.cpus, memory_bytes: o.memory_bytes } : undefined };
  });
}

export default function ResourceLimitsPage() {
  const { limits: t, common } = useT();
  const a = t.admin;
  const { can } = usePermissions();
  const manage = can("ManageResourceLimits");
  const query = useQuery(adminLimitsQuery());
  const roles = useQuery({ ...rolesQuery(), enabled: can("ViewRoles") });
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["resource-limits"] });

  async function save(target: Editing["target"], size: { cpus: number; memory_bytes: number }, userId?: string) {
    const to: LimitTarget = target.scope === "new-user" ? { scope: "user", id: userId ?? "" } : target;
    await setLimit(to, size);
    toast(a.saved);
    await refresh();
  }

  async function remove(target: Exclude<LimitTarget, { scope: "default" }>) {
    try {
      await removeLimit(target);
      toast(a.removed);
      await refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "warn");
    }
  }

  const header = <PageHeader title={SECTIONS.resourceLimits.title} breadcrumb={sectionCrumbs("resourceLimits")} description={SECTIONS.resourceLimits.subtitle} />;
  if (query.isPending) return (<>{header}<Loading /></>);
  if (query.isError) return (<>{header}<ErrorState title={a.loadFailed} message={query.error.message} /></>);
  const limits = query.data;
  const sized = (s: { cpus: number; memory_bytes: number }) => t.size(s.cpus, toGib(s.memory_bytes));

  return (
    <>
      {header}
      <section className={cn(cardClass, "mb-5 flex items-start gap-3 p-4")} aria-label={a.host(sized(limits.host))}>
        <IconTile className="bg-primary-lighter text-primary-dark">
          <Cpu weight="duotone" className="h-5 w-5" />
        </IconTile>
        <div className="min-w-0 text-sm text-gray-700">
          <div>{a.host(sized(limits.host))}</div>
          {manage ? null : (
            <div className="mt-1 flex items-center gap-1.5 text-[13px] text-gray-500">
              <Info className="h-4 w-4 shrink-0" aria-hidden /> {a.readOnly}
            </div>
          )}
        </div>
      </section>
      <div className="grid gap-5 xl:grid-cols-12">
        <WidgetCard className="xl:col-span-4" title={a.defaultTitle} description={a.defaultHint}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="font-display text-2xl font-semibold text-gray-900" data-testid="default-limit">
              {sized(limits.default)}
            </div>
            {manage ? (
              <Button size="sm" variant="outline" onClick={() => setEditing({ title: t.dialog.defaultTitle, initial: limits.default, target: { scope: "default" } })}>
                {common.actions.edit}
              </Button>
            ) : null}
          </div>
        </WidgetCard>
        <WidgetCard className="min-w-0 xl:col-span-8" title={a.rolesTitle} description={a.rolesHint}>
          <RolesTable
            rows={roleRows(limits, roles.data)}
            manage={manage}
            onEdit={(r) => setEditing({ title: t.dialog.roleTitle(r.name), initial: r.limit ?? limits.default, target: { scope: "role", id: r.id } })}
            onRemove={(r) => void remove({ scope: "role", id: r.id })}
          />
        </WidgetCard>
        <WidgetCard
          className="min-w-0 xl:col-span-12"
          title={a.usersTitle}
          description={a.usersHint}
          action={
            manage ? (
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() =>
                  setEditing({ title: t.dialog.userTitle, initial: limits.default, target: { scope: "new-user" }, pickUser: { exclude: limits.users.map((u) => u.user_id) } })
                }
              >
                <Plus weight="bold" className="h-4 w-4" /> {a.addUser}
              </Button>
            ) : null
          }
        >
          {limits.users.length === 0 ? (
            <EmptyState compact icon={<UserCircle weight="duotone" />} title={a.noUsers}>
              {a.noUsersBody}
            </EmptyState>
          ) : (
            <UsersTable
              rows={limits.users}
              manage={manage}
              onEdit={(u) => setEditing({ title: t.dialog.roleTitle(u.display_name || u.email), initial: u, target: { scope: "user", id: u.user_id } })}
              onRemove={(u) => void remove({ scope: "user", id: u.user_id })}
            />
          )}
        </WidgetCard>
      </div>
      {editing ? (
        <LimitDialog
          edit={editing}
          min={limitFloor(limits.floor)}
          max={limits.host}
          onClose={() => setEditing(undefined)}
          onSave={(size, userId) => save(editing.target, size, userId)}
        />
      ) : null}
    </>
  );
}
